import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { PaymentDraft, Transaction } from '../types';
import { emptyDraft, paymentService } from '../services/payment/paymentService';
import { transactionService } from '../services/payment/transactionService';
import { MOCK_TRANSACTIONS } from '../lib/mockData';

interface PaymentContextValue {
  /** In-flight send-payment draft, shared across the multi-step flow. */
  draft: PaymentDraft;
  setDraft: (patch: Partial<PaymentDraft>) => void;
  resetDraft: () => void;
  /** Recently completed transactions (most recent first). */
  recent: Transaction[];
  addTransaction: (tx: Transaction) => void;
  /** Aggregate balance figure shown on the dashboard. */
  balance: number;
  totalSent: number;
  totalReceived: number;
  processDraft: () => ReturnType<typeof paymentService.process>;
}

const PaymentContext = createContext<PaymentContextValue | undefined>(undefined);

export function PaymentProvider({ children }: { children: ReactNode }) {
  const [draft, setDraftState] = useState<PaymentDraft>(emptyDraft());
  const [recent, setRecent] = useState<Transaction[]>(MOCK_TRANSACTIONS.slice(0, 6));
  const [balance, setBalance] = useState(12500.75);

  const setDraft = useCallback((patch: Partial<PaymentDraft>) => {
    setDraftState((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetDraft = useCallback(() => setDraftState(emptyDraft()), []);

  const addTransaction = useCallback((tx: Transaction) => {
    setRecent((prev) => [tx, ...prev].slice(0, 12));
    transactionService.prepend(tx);
    setBalance((prev) => (tx.direction === 'sent' ? prev - tx.amount - tx.fee : prev + tx.amount));
  }, []);

  const processDraft = useCallback(async () => {
    const result = await paymentService.process(draft);
    if (result.ok) addTransaction(result.data.transaction);
    return result;
  }, [draft, addTransaction]);

  const { totalSent, totalReceived } = useMemo(() => {
    return MOCK_TRANSACTIONS.reduce(
      (acc, t) => {
        if (t.direction === 'sent') acc.totalSent += t.amount;
        else acc.totalReceived += t.amount;
        return acc;
      },
      { totalSent: 0, totalReceived: 0 },
    );
  }, []);

  const value = useMemo<PaymentContextValue>(
    () => ({ draft, setDraft, resetDraft, recent, addTransaction, balance, totalSent, totalReceived, processDraft }),
    [draft, setDraft, resetDraft, recent, addTransaction, balance, totalSent, totalReceived, processDraft],
  );

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
}

export function usePaymentContext() {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error('usePaymentContext must be used within a PaymentProvider');
  return ctx;
}
